import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Auth Components & Guards
import AuthPage from '../components/auth/AuthPage';
import { 
  ProtectedRoute, 
  CoachRoute, 
  AthleteRoute, 
  GuestRoute,
  TeamMemberRoute,
  TeamOwnerRoute
} from '../components/auth/RouteGuards';
import ResetPassword from '../components/ResetPassword';
import QuickStartGuide from '../components/QuickStartGuide';

// Dashboard Components
import DashboardRouter from './DashboardRouter';
import CoachDashboard from '../components/coach/CoachDashboard';
import Dashboard from '../components/athlete/Dashboard';

/**
 * AppRouter - Central routing configuration for the application
 * 
 * Route Guard Usage:
 * - GuestRoute: Only accessible when NOT logged in (auth pages)
 * - ProtectedRoute: Requires any authenticated user
 * - CoachRoute: Requires coach role
 * - AthleteRoute: Requires athlete role
 * - TeamMemberRoute: Requires user to be member/owner of specified team
 * - TeamOwnerRoute: Requires user to be the coach/owner of specified team
 */
const AppRouter = () => {
  return (
    <Routes>
      {/* ========== Public Routes ========== */}
      
      {/* Auth - Only for guests (redirects logged-in users to dashboard) */}
      <Route 
        path="/auth" 
        element={
          <GuestRoute>
            <AuthPage />
          </GuestRoute>
        } 
      />
      
      {/* Password Reset - Public but redirects if logged in */}
      <Route 
        path="/reset-password" 
        element={
          <GuestRoute>
            <ResetPassword />
          </GuestRoute>
        } 
      />
      
      {/* Quick Start Guide - Public */}
      <Route path="/quick-start" element={<QuickStartGuide />} />
      
      
      {/* ========== Protected Routes (Any Authenticated User) ========== */}
      
      {/* Main Dashboard - Routes to appropriate dashboard based on role */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      
      
      {/* ========== Coach-Only Routes ========== */}
      
      <Route
        path="/coach/*"
        element={
          <CoachRoute>
            <CoachDashboard />
          </CoachRoute>
        }
      />
      
      {/* Coach Team Management */}
      <Route
        path="/teams/manage"
        element={
          <CoachRoute>
            <CoachDashboard />
          </CoachRoute>
        }
      />
      
      
      {/* ========== Athlete-Only Routes ========== */}
      
      <Route
        path="/athlete/*"
        element={
          <AthleteRoute>
            <Dashboard />
          </AthleteRoute>
        }
      />
      
      {/* Athlete Training & Stats */}
      <Route
        path="/training"
        element={
          <AthleteRoute>
            <Dashboard />
          </AthleteRoute>
        }
      />
      
      <Route
        path="/my-stats"
        element={
          <AthleteRoute>
            <Dashboard />
          </AthleteRoute>
        }
      />
      
      
      {/* ========== Team-Specific Routes ========== */}
      
      {/* Team details page - accessible by team members (athletes) and team owner (coach) */}
      <Route
        path="/team/:teamId"
        element={
          <TeamMemberRoute>
            <DashboardRouter />
          </TeamMemberRoute>
        }
      />
      
      {/* Team settings - only accessible by team owner (coach) */}
      <Route
        path="/team/:teamId/settings"
        element={
          <TeamOwnerRoute>
            <CoachDashboard />
          </TeamOwnerRoute>
        }
      />
      
      
      {/* ========== Default Redirects ========== */}
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default AppRouter;
